<script setup lang="ts">
/** Centred dialog. Focus trap, Escape, focus return and scroll lock come from Reka UI. */
import { DialogClose, DialogContent, DialogDescription, DialogOverlay, DialogPortal,
  DialogRoot, DialogTitle } from 'reka-ui'
import { useI18n } from 'vue-i18n'
import Icon from './Icon.vue'

withDefaults(defineProps<{
  open: boolean; title: string; description?: string; size?: 'sm' | 'md' | 'lg' | 'xl'
}>(), { size: 'md' })
const emit = defineEmits<{ close: [] }>()
const { t } = useI18n()
/** A click on a toast (or its close button) is not a click outside: the dialog stays open. */
function interactOutside(e: CustomEvent<{ originalEvent?: Event }>) {
  const target = (e.detail?.originalEvent?.target ?? e.target) as Element | null
  if (target?.closest?.('[data-toast-region]')) e.preventDefault()
}
const WIDTH = { sm: 'max-w-[420px]', md: 'max-w-[560px]', lg: 'max-w-[760px]', xl: 'max-w-[980px]' }
</script>

<template>
  <DialogRoot :open="open" @update:open="(o: boolean) => { if (!o) emit('close') }">
    <DialogPortal>
      <DialogOverlay class="fixed inset-0 z-dialog bg-foreground/25 backdrop-blur-[1px]
                            data-[state=open]:animate-in data-[state=open]:fade-in-0
                            data-[state=closed]:animate-out data-[state=closed]:fade-out-0" />
      <!-- No description given: said to the dialog, rather than pointing at a description that is not there. -->
      <!-- Centred by its margins, not a transform: its opening and closing scale freely (inset-0, m-auto, h-fit). -->
      <DialogContent v-bind="description ? {} : { 'aria-describedby': undefined }" @interact-outside="interactOutside" :class="[WIDTH[size], 'fixed inset-0 z-dialog m-auto flex h-fit max-h-[85vh] w-[calc(100vw-32px)]',
                               'flex-col rounded-card border border-border bg-card shadow-pop',
                               'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-[.97] data-[state=open]:slide-in-from-bottom-2',
                               'data-[state=open]:duration-200 data-[state=open]:ease-spring',
                               'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-[.97] data-[state=closed]:duration-150']">
        <div class="flex items-start gap-3 border-b border-border px-5 py-4">
          <div class="min-w-0 flex-1">
            <DialogTitle class="text-[15px] font-semibold text-foreground"><slot name="title">{{ title }}</slot></DialogTitle>
            <DialogDescription v-if="description" class="mt-1 text-[12.5px] leading-[1.45] text-muted-foreground">
              {{ description }}
            </DialogDescription>
          </div>
          <DialogClose :aria-label="t('common.close')"
                       class="grid h-7 w-7 flex-none place-items-center rounded-input text-muted-foreground
                              transition-colors hover:bg-muted hover:text-foreground">
            <Icon name="x" :size="15" />
          </DialogClose>
        </div>
        <div class="grid content-start gap-3 overflow-auto px-5 py-4"><slot /></div>
        <div v-if="$slots.footer" class="flex flex-wrap items-center justify-end gap-2 border-t border-border px-5 py-3">
          <slot name="footer" />
        </div>
      </DialogContent>
    </DialogPortal>
  </DialogRoot>
</template>
